import * as React from "react";
import { createContext, type SyntheticEvent, useCallback, useContext, useMemo, useState } from "react";

import classNames from "classnames";

import { useTranslation } from "react-i18next";
import { Box, List, ListItem, ListItemButton, ListItemText, Stack, styled, Typography } from "@mui/material";
import type { ProductGroup } from "~/Search/models";
import FilterGroupItem from "../../FilterGroupItem";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { C_ON_SURFACE_LOW_EMPHASIS } from "~/common/components/icon/Colors";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import {
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2GroupTransition,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Provider,
  TreeItem2Root,
  useTreeItem2,
  type UseTreeItem2Parameters,
  useTreeViewApiRef,
} from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { produce } from "immer";
import { flexEnd } from "~/styles/styledMixins";
import { selectActiveProductGroups } from "~/Search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedProductGroupsThunk } from "~/Search/redux/actions";
import { TreeSelectionManager } from "~/Search/components/Filters/components/CategoryFilter/TreeSelectionManager";
import { Button } from "~/common/components/Button/Button";

export const ReduxProductCategoryFilter = (props: Omit<Props, "values" | "setValues">) => {
  const externalValues = useSelector(selectActiveProductGroups);
  const dispatch = useDispatch();
  const stateProcessor = useMemo(
    () => TreeSelectionManager.for(props.taxonomy, (item) => item.fullFacetUid),
    [props.taxonomy],
  );
  const values = useMemo(() => {
    let decompressedValues: string[];
    if (externalValues.length === 0) {
      decompressedValues = [];
    } else if (externalValues.length === 1) {
      const parts = externalValues[0].split("/");
      if (parts.length > 3) {
        decompressedValues = [parts.slice(0, 3).join("/"), ...stateProcessor.decompress(externalValues)];
      } else {
        decompressedValues = externalValues;
      }
    } else {
      decompressedValues = [externalValues[0], ...stateProcessor.decompress(externalValues.slice(1))];
    }
    return decompressedValues;
  }, [stateProcessor, externalValues]);
  const setValues = useCallback(
    (values: string[]) => {
      let compressedValues: string[];
      if (values.length === 0) {
        compressedValues = [];
      } else if (values.length === 1) {
        // const parts = externalValues[0].split("/");
        // if (parts.length > 3) {
        //   compressedValues = [parts.slice(0, 3).join("/"), ...stateProcessor.compress(values)];
        // }
        compressedValues = values;
      } else {
        compressedValues = [values[0], ...stateProcessor.compress(values.slice(1))];
      }
      return dispatch(setSelectedProductGroupsThunk(compressedValues));
    },
    [stateProcessor],
  );
  return <ProductCategoryFilter {...props} values={values} setValues={setValues} />;
};

const CategoryFilterContext = createContext<string[]>([]);
const useSelectedValues = () => useContext(CategoryFilterContext);

interface Props {
  taxonomy: ProductGroup[];
  values: string[];
  setValues: (newValues: string[]) => void;
  initiallyExpanded?: boolean;
}

export const ProductCategoryFilter = (props: Props) => {
  const { t } = useTranslation();
  const { taxonomy, values } = props;
  if (taxonomy.length === 0) {
    return null;
  }

  if (values.length === 0) {
    return (
      <StyledFilterGroupItem
        title={t("page.search.categoryFilter.title")}
        data-test-id={"taxonomy-filter"}
        initiallyExpanded={props.initiallyExpanded}
      >
        <DeselectedFilter {...props} />
      </StyledFilterGroupItem>
    );
  }

  const rootCategory = values[0];
  const rootNode = taxonomy.find((node) => node.fullFacetUid === rootCategory);

  if (!rootNode) {
    console.warn("Invalid root category");
    return null;
  }

  return (
    <StyledFilterGroupItem
      title={t("page.search.categoryFilter.title")}
      data-test-id={"taxonomy-filter"}
      initiallyExpanded={props.initiallyExpanded}
    >
      <SelectedFilter {...props} />
    </StyledFilterGroupItem>
  );
};

/////////////////////
// DESELECTED FILTER
////////////////////

const StyledFilterGroupItem = styled(FilterGroupItem)({
  paddingInline: 0,
});

const DeselectedFilter = ({ taxonomy, setValues }: Props) => {
  const { t } = useTranslation();

  return (
    <List>
      {taxonomy.map((groupEntry) => (
        <ListItem key={groupEntry.fullFacetUid} disablePadding>
          <ListItemButton onClick={() => setValues([groupEntry.fullFacetUid])}>
            <ListItemText primary={groupEntry.name} />
            <ArrowRightIcon />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

/////////////////////
// SELECTED FILTER
////////////////////

const SelectedFilter = ({ taxonomy, setValues, values }: Props) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState<string[]>(values);

  const rootCategory = values[0];
  const rootNode = taxonomy.find((node) => node.fullFacetUid === rootCategory)!;

  const getId = useCallback((node: ProductGroup) => node.fullFacetUid, []);
  const getLabel = useCallback((node: ProductGroup) => node.name, []);

  const apiRef = useTreeViewApiRef();

  const handleOnItemSelectionToggle = (event: SyntheticEvent, itemId: string, selected: boolean) => {
    const updated = produce(values, (draft) => {
      const item = apiRef.current?.getItem(itemId);
      const ids = getAllThisAndChildIds(item!);
      if (selected) {
        draft.push(...ids);
        let parentId = itemId;
        while (true) {
          parentId = getParentId(parentId);
          let parent;
          if (parentId === rootCategory) {
            parent = rootNode;
          } else {
            parent = apiRef.current?.getItem(parentId) as ProductGroup | undefined;
          }
          const allSelected = parent && parent.children.every((child) => draft.includes(child.fullFacetUid));
          if (allSelected) {
            if (parent === rootNode) {
              draft.splice(1, draft.length);
              break;
            } else {
              draft.push(parent!.fullFacetUid);
            }
          } else {
            break;
          }
        }
      } else {
        // not selected
        ids.push(...getAllParentIds(itemId, rootCategory));
        draft = draft.filter((id) => !ids.includes(id));
        return draft;
      }
    });
    setValues(updated);
  };

  return (
    <CategoryFilterContext.Provider value={values}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant={"bodySmall"} sx={{ color: C_ON_SURFACE_LOW_EMPHASIS }}>
          {rootNode.name}
        </Typography>
      </Box>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <RichTreeView
          apiRef={apiRef}
          items={rootNode.children}
          getItemId={getId}
          getItemLabel={getLabel}
          expandedItems={expandedItems}
          onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
          itemChildrenIndentation={40}
          multiSelect
          slots={{ item: CustomTreeItem }}
          checkboxSelection
          selectedItems={values}
          onItemSelectionToggle={handleOnItemSelectionToggle}
        />
        <Box sx={{ ...flexEnd() }}>
          <Button variant={"text"} onClick={() => setValues([])}>
            Clear
          </Button>
        </Box>
      </Stack>
    </CategoryFilterContext.Provider>
  );
};

interface CustomTreeItemProps
  extends Omit<UseTreeItem2Parameters, "rootRef">,
    Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: CustomTreeItemProps,
  ref: React.Ref<HTMLLIElement>,
) {
  const { id, itemId, label, disabled, children, ...other } = props;
  const selectedItems = useSelectedValues();

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  const item = publicAPI.getItem(itemId) as ProductGroup;

  const expandable = item.children.length > 0;

  const checkboxProps = getCheckboxProps();
  const isPartiallyChecked = !status.selected && calculateIsPartiallySelected(item, selectedItems);

  return (
    <TreeItem2Provider itemId={itemId}>
      <TreeItem2Root {...getRootProps(other)}>
        <StyledTreeItem2Content
          {...getContentProps({
            className: classNames("content", {
              "Mui-expanded": status.expanded,
              "Mui-selected": status.selected,
              "Mui-focused": status.focused,
              "Mui-disabled": status.disabled,
            }),
          })}
        >
          <TreeItem2Checkbox {...getCheckboxProps()} indeterminate={isPartiallyChecked} />
          <CustomLabel {...getLabelProps({ expandable: expandable && status.expanded })}>
            <Typography variant={"bodyLarge"} sx={{ lineClamp: "3" }}>
              {label}
            </Typography>
          </CustomLabel>

          {expandable && (
            <TreeItem2IconContainer {...getIconContainerProps()}>
              <ExpandedIcon expanded={status.expanded} />
            </TreeItem2IconContainer>
          )}
        </StyledTreeItem2Content>
        {children && <TreeItem2GroupTransition {...getGroupTransitionProps()} />}
      </TreeItem2Root>
    </TreeItem2Provider>
  );
});

interface ExpandedIconProps {
  expanded?: boolean;
}

const ExpandedIcon = styled(ArrowDropDownIcon, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<ExpandedIconProps>(({ theme, expanded }) => ({
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
}));

const CustomLabel = styled(TreeItem2Label)({
  minHeight: "40px",
  display: "flex",
  alignItems: "center",
});

const StyledTreeItem2Content = styled(TreeItem2Content)({
  padding: "8px 16px",
  ["&:where(.Mui-selected)"]: {
    background: "unset",
  },
});

function getParentId(itemId: string) {
  return itemId.split("/").slice(0, -1).join("/");
}

function getAllThisAndChildIds(item: ProductGroup, acc: string[] = []): string[] {
  acc.push(item.fullFacetUid);
  item.children.forEach((child) => getAllThisAndChildIds(child, acc));
  return acc;
}
function getAllParentIds(itemId: string, finalParentId: string, acc: string[] = []): string[] {
  const parentId = getParentId(itemId);
  if (!!parentId && parentId !== finalParentId) {
    acc.push(parentId);
    getAllParentIds(parentId, finalParentId, acc);
  }
  return acc;
}

function calculateIsPartiallySelected(item: ProductGroup, selectedItems: string[]): boolean {
  if (item.children.length === 0) {
    return false;
  }
  const fullySelected = item.children.every((child) => selectedItems.includes(child.fullFacetUid));
  if (fullySelected) {
    return false;
  }
  return selectedItems.some((selectedCode) => selectedCode.includes(item.fullFacetUid));
}
